/**
 * Entitlement System
 *
 * User → Roles → Entitlements
 * Tenant = Company
 *
 * Each entitlement controls access to specific business operations.
 * Entitlements are returned as an array of IDs from the login endpoint.
 */

// Entitlement ID to Name mapping
export const ENTITLEMENTS = {
  // Tenant Management (1-9)
  TENANT_READ: 1,
  TENANT_CREATE: 2,
  TENANT_UPDATE: 3,
  TENANT_DELETE: 4,
  TENANT_UPDATE_STATUS: 5,

  // User Management (10-19)
  USER_READ: 10,
  USER_CREATE: 11,
  USER_UPDATE: 12,
  USER_DELETE: 13,
  USER_UPDATE_STATUS: 14,
  USER_CHANGE_PASSWORD: 15,

  // Role Management (20-29)
  ROLE_READ: 20,
  ROLE_CREATE: 21,
  ROLE_UPDATE: 22,
  ROLE_DELETE: 23,
  ROLE_UPDATE_STATUS: 24,

  // Rule Category Management (30-39)
  RULE_CATEGORY_READ: 30,
  RULE_CATEGORY_CREATE: 31,
  RULE_CATEGORY_UPDATE: 32,
  RULE_CATEGORY_DELETE: 33,
  RULE_CATEGORY_UPDATE_STATUS: 34,

  // Rule Management (40-49)
  RULE_READ: 40,
  RULE_CREATE: 41,
  RULE_UPDATE: 42,
  RULE_DELETE: 43,
  RULE_UPDATE_STATUS: 44,

  // Prompt Management (50-59)
  PROMPT_READ: 50,
  PROMPT_CREATE: 51,
  PROMPT_UPDATE: 52,
  PROMPT_DELETE: 53,
  PROMPT_UPDATE_STATUS: 54,

  // Case Management (60-69)
  CASE_READ: 60,
  CASE_CREATE: 61,
  CASE_UPDATE: 62,
  CASE_DELETE: 63,
  CASE_UPDATE_STATUS: 64,

  // Document Management (70-79)
  DOCUMENT_READ: 70,
  DOCUMENT_CREATE: 71,
  DOCUMENT_UPDATE: 72,
  DOCUMENT_DELETE: 73,
  DOCUMENT_UPDATE_STATUS: 74,

  // Questionnaire Management (80-89)
  QUESTIONNAIRE_READ: 80,
  QUESTIONNAIRE_CREATE: 81,
  QUESTIONNAIRE_UPDATE: 82,
  QUESTIONNAIRE_DELETE: 83,
  QUESTIONNAIRE_UPDATE_STATUS: 84,
  QUESTIONNAIRE_ANSWER: 85,

  // Document Type Management (90-99)
  DOCUMENT_TYPE_READ: 90,
  DOCUMENT_TYPE_CREATE: 91,
  DOCUMENT_TYPE_UPDATE: 92,
  DOCUMENT_TYPE_DELETE: 93,
  DOCUMENT_TYPE_UPDATE_STATUS: 94,

  // Subscription Package Management (100-109)
  SUBSCRIPTION_PACKAGE_READ: 100,
  SUBSCRIPTION_PACKAGE_CREATE: 101,
  SUBSCRIPTION_PACKAGE_UPDATE: 102,
  SUBSCRIPTION_PACKAGE_DELETE: 103,
  SUBSCRIPTION_PACKAGE_UPDATE_STATUS: 104,

  // Subscription Management (110-119)
  SUBSCRIPTION_READ: 110,
  SUBSCRIPTION_CREATE: 111,
  SUBSCRIPTION_UPDATE: 112,
  SUBSCRIPTION_DELETE: 113,
  SUBSCRIPTION_UPDATE_STATUS: 114,
  SUBSCRIPTION_ACTIVATE: 115,

  // Job Management (120-129)
  JOB_READ: 120,
  JOB_CREATE: 121,
  JOB_UPDATE: 122,
  JOB_DELETE: 123,
  JOB_UPDATE_STATUS: 124,

  // Chat Management (130-139)
  CHAT_READ: 130,
  CHAT_CREATE: 131,
  CHAT_UPDATE: 132,
  CHAT_DELETE: 133,
  CHAT_UPDATE_STATUS: 134,

  // System Administration (140-149)
  SYSTEM_READ: 140,
  SYSTEM_CREATE: 141,
  SYSTEM_UPDATE: 142,
  SYSTEM_DELETE: 143,
  SYSTEM_UPDATE_STATUS: 144,

  // Rule Set Management (150-159)
  RULE_SET_READ: 150,
  RULE_SET_CREATE: 151,
  RULE_SET_UPDATE: 152,
  RULE_SET_DELETE: 153,
  RULE_SET_UPDATE_STATUS: 154,

  // Assessment Management (190-199)
  ASSESSMENT_READ: 190,
  ASSESSMENT_CREATE: 191,
  ASSESSMENT_UPDATE: 192,

  // Audit Log Management (200-209)
  AUDIT_READ: 200,

  // Assessment Template Management (210-219)
  ASSESSMENT_TEMPLATE_READ: 210,
  ASSESSMENT_TEMPLATE_CREATE: 211,
  ASSESSMENT_TEMPLATE_UPDATE: 212,

  // Label Tagging Management (220-229)
  LABEL_TAGGING_READ: 220,
  LABEL_TAGGING_CREATE: 221,
  LABEL_TAGGING_UPDATE: 222,
  LABEL_TAGGING_DELETE: 223,

  // Pricing Config Management (230-239)
  PRICING_CONFIG_READ: 230,
  PRICING_CONFIG_CREATE: 231,
  PRICING_CONFIG_UPDATE: 232,
  PRICING_CONFIG_DELETE: 233,
  PRICING_CONFIG_UPDATE_STATUS: 234,

  // API Call Cost Management (240-249)
  API_CALL_COST_READ: 240,

  // Service Provider Management (250-259)
  SERVICE_PROVIDER_READ: 250,
  SERVICE_PROVIDER_CREATE: 251,
  SERVICE_PROVIDER_UPDATE: 252,
  SERVICE_PROVIDER_DELETE: 253,
  SERVICE_PROVIDER_UPDATE_STATUS: 254,

  // Service Model Management (260-269)
  SERVICE_MODEL_READ: 260,
  SERVICE_MODEL_CREATE: 261,
  SERVICE_MODEL_UPDATE: 262,
  SERVICE_MODEL_DELETE: 263,
  SERVICE_MODEL_UPDATE_STATUS: 264,

  // Dashboard Preference Management (270-279)
  DASHBOARD_PREFERENCE_READ: 270,

  // AI Model Routing / Feature AI Configuration (280-289)
  FEATURE_AI_CONFIGURATION_READ: 280,
  FEATURE_AI_CONFIGURATION_CREATE: 281,
  FEATURE_AI_CONFIGURATION_UPDATE: 282,
  FEATURE_AI_CONFIGURATION_DELETE: 283,
  FEATURE_AI_CONFIGURATION_UPDATE_STATUS: 284,
} as const;

// Reverse mapping: ID → Name
export const ENTITLEMENT_ID_TO_NAME: Record<number, string> = {
  1: "Tenant.Read",
  2: "Tenant.Create",
  3: "Tenant.Update",
  4: "Tenant.Delete",
  5: "Tenant.UpdateStatus",
  10: "User.Read",
  11: "User.Create",
  12: "User.Update",
  13: "User.Delete",
  14: "User.UpdateStatus",
  15: "User.ChangePassword",
  20: "Role.Read",
  21: "Role.Create",
  22: "Role.Update",
  23: "Role.Delete",
  24: "Role.UpdateStatus",
  30: "RuleCategory.Read",
  31: "RuleCategory.Create",
  32: "RuleCategory.Update",
  33: "RuleCategory.Delete",
  34: "RuleCategory.UpdateStatus",
  40: "Rule.Read",
  41: "Rule.Create",
  42: "Rule.Update",
  43: "Rule.Delete",
  44: "Rule.UpdateStatus",
  50: "Prompt.Read",
  51: "Prompt.Create",
  52: "Prompt.Update",
  53: "Prompt.Delete",
  54: "Prompt.UpdateStatus",
  60: "Case.Read",
  61: "Case.Create",
  62: "Case.Update",
  63: "Case.Delete",
  64: "Case.UpdateStatus",
  70: "Document.Read",
  71: "Document.Create",
  72: "Document.Update",
  73: "Document.Delete",
  74: "Document.Update Status",
  80: "Questionnaire.Read",
  81: "Questionnaire.Create",
  82: "Questionnaire.Update",
  83: "Questionnaire.Delete",
  84: "Questionnaire.UpdateStatus",
  85: "Questionnaire.Answer",
  90: "DocumentType.Read",
  91: "DocumentType.Create",
  92: "DocumentType.Update",
  93: "DocumentType.Delete",
  94: "DocumentType.UpdateStatus",
  100: "SubscriptionPackage.Read",
  101: "SubscriptionPackage.Create",
  102: "SubscriptionPackage.Update",
  103: "SubscriptionPackage.Delete",
  104: "SubscriptionPackage.UpdateStatus",
  110: "Subscription.Read",
  111: "Subscription.Create",
  112: "Subscription.Update",
  113: "Subscription.Delete",
  114: "Subscription.UpdateStatus",
  115: "Subscription.Activate",
  120: "Job.Read",
  121: "Job.Create",
  122: "Job.Update",
  123: "Job.Delete",
  124: "Job.UpdateStatus",
  130: "Chat.Read",
  131: "Chat.Create",
  132: "Chat.Update",
  133: "Chat.Delete",
  134: "Chat.UpdateStatus",
  140: "System.Read",
  141: "System.Create",
  142: "System.Update",
  143: "System.Delete",
  144: "System.UpdateStatus",
  150: "RuleSet.Read",
  151: "RuleSet.Create",
  152: "RuleSet.Update",
  153: "RuleSet.Delete",
  154: "RuleSet.UpdateStatus",
  190: "Assessment.Read",
  191: "Assessment.Create",
  192: "Assessment.Update",
  200: "Audit.Read",
  210: "AssessmentTemplate.Read",
  211: "AssessmentTemplate.Create",
  212: "AssessmentTemplate.Update",
  220: "LabelTagging.Read",
  221: "LabelTagging.Create",
  222: "LabelTagging.Update",
  223: "LabelTagging.Delete",
  230: "PricingConfig.Read",
  231: "PricingConfig.Create",
  232: "PricingConfig.Update",
  233: "PricingConfig.Delete",
  234: "PricingConfig.UpdateStatus",
  240: "ApiCallCost.Read",
  250: "ServiceProvider.Read",
  251: "ServiceProvider.Create",
  252: "ServiceProvider.Update",
  253: "ServiceProvider.Delete",
  254: "ServiceProvider.UpdateStatus",
  260: "ServiceModel.Read",
  261: "ServiceModel.Create",
  262: "ServiceModel.Update",
  263: "ServiceModel.Delete",
  264: "ServiceModel.UpdateStatus",
  270: "DashboardPreference.Read",
  280: "FeatureAiConfiguration.Read",
  281: "FeatureAiConfiguration.Create",
  282: "FeatureAiConfiguration.Update",
  283: "FeatureAiConfiguration.Delete",
  284: "FeatureAiConfiguration.UpdateStatus",
};

// Forward mapping: Name → ID
export const ENTITLEMENT_NAME_TO_ID: Record<string, number> = {
  "Tenant.Read": 1,
  "Tenant.Create": 2,
  "Tenant.Update": 3,
  "Tenant.Delete": 4,
  "Tenant.UpdateStatus": 5,
  "User.Read": 10,
  "User.Create": 11,
  "User.Update": 12,
  "User.Delete": 13,
  "User.UpdateStatus": 14,
  "User.ChangePassword": 15,
  "Role.Read": 20,
  "Role.Create": 21,
  "Role.Update": 22,
  "Role.Delete": 23,
  "Role.UpdateStatus": 24,
  "RuleCategory.Read": 30,
  "RuleCategory.Create": 31,
  "RuleCategory.Update": 32,
  "RuleCategory.Delete": 33,
  "RuleCategory.UpdateStatus": 34,
  "Rule.Read": 40,
  "Rule.Create": 41,
  "Rule.Update": 42,
  "Rule.Delete": 43,
  "Rule.UpdateStatus": 44,
  "Prompt.Read": 50,
  "Prompt.Create": 51,
  "Prompt.Update": 52,
  "Prompt.Delete": 53,
  "Prompt.UpdateStatus": 54,
  "Case.Read": 60,
  "Case.Create": 61,
  "Case.Update": 62,
  "Case.Delete": 63,
  "Case.UpdateStatus": 64,
  "Document.Read": 70,
  "Document.Create": 71,
  "Document.Update": 72,
  "Document.Delete": 73,
  "Document.UpdateStatus": 74,
  "Questionnaire.Read": 80,
  "Questionnaire.Create": 81,
  "Questionnaire.Update": 82,
  "Questionnaire.Delete": 83,
  "Questionnaire.UpdateStatus": 84,
  "Questionnaire.Answer": 85,
  "DocumentType.Read": 90,
  "DocumentType.Create": 91,
  "DocumentType.Update": 92,
  "DocumentType.Delete": 93,
  "DocumentType.UpdateStatus": 94,
  "SubscriptionPackage.Read": 100,
  "SubscriptionPackage.Create": 101,
  "SubscriptionPackage.Update": 102,
  "SubscriptionPackage.Delete": 103,
  "SubscriptionPackage.UpdateStatus": 104,
  "Subscription.Read": 110,
  "Subscription.Create": 111,
  "Subscription.Update": 112,
  "Subscription.Delete": 113,
  "Subscription.UpdateStatus": 114,
  "Subscription.Activate": 115,
  "Job.Read": 120,
  "Job.Create": 121,
  "Job.Update": 122,
  "Job.Delete": 123,
  "Job.UpdateStatus": 124,
  "Chat.Read": 130,
  "Chat.Create": 131,
  "Chat.Update": 132,
  "Chat.Delete": 133,
  "Chat.UpdateStatus": 134,
  "System.Read": 140,
  "System.Create": 141,
  "System.Update": 142,
  "System.Delete": 143,
  "System.UpdateStatus": 144,
  "RuleSet.Read": 150,
  "RuleSet.Create": 151,
  "RuleSet.Update": 152,
  "RuleSet.Delete": 153,
  "RuleSet.UpdateStatus": 154,
  "Assessment.Read": 190,
  "Assessment.Create": 191,
  "Assessment.Update": 192,
  "Audit.Read": 200,
  "AssessmentTemplate.Read": 210,
  "AssessmentTemplate.Create": 211,
  "AssessmentTemplate.Update": 212,
  "LabelTagging.Read": 220,
  "LabelTagging.Create": 221,
  "LabelTagging.Update": 222,
  "LabelTagging.Delete": 223,
  "PricingConfig.Read": 230,
  "PricingConfig.Create": 231,
  "PricingConfig.Update": 232,
  "PricingConfig.Delete": 233,
  "PricingConfig.UpdateStatus": 234,
  "ApiCallCost.Read": 240,
  "ServiceProvider.Read": 250,
  "ServiceProvider.Create": 251,
  "ServiceProvider.Update": 252,
  "ServiceProvider.Delete": 253,
  "ServiceProvider.UpdateStatus": 254,
  "ServiceModel.Read": 260,
  "ServiceModel.Create": 261,
  "ServiceModel.Update": 262,
  "ServiceModel.Delete": 263,
  "ServiceModel.UpdateStatus": 264,
  "DashboardPreference.Read": 270,
  "FeatureAiConfiguration.Read": 280,
  "FeatureAiConfiguration.Create": 281,
  "FeatureAiConfiguration.Update": 282,
  "FeatureAiConfiguration.Delete": 283,
  "FeatureAiConfiguration.UpdateStatus": 284,
};

// Entity groupings for checking module-level access
export const ENTITLEMENT_ENTITIES = {
  Tenant: [1, 2, 3, 4, 5],
  User: [10, 11, 12, 13, 14, 15],
  Role: [20, 21, 22, 23, 24],
  RuleCategory: [30, 31, 32, 33, 34],
  Rule: [40, 41, 42, 43, 44],
  Prompt: [50, 51, 52, 53, 54],
  Case: [60, 61, 62, 63, 64],
  Document: [70, 71, 72, 73, 74],
  Questionnaire: [80, 81, 82, 83, 84, 85],
  DocumentType: [90, 91, 92, 93, 94],
  SubscriptionPackage: [100, 101, 102, 103, 104],
  Subscription: [110, 111, 112, 113, 114, 115],
  Job: [120, 121, 122, 123, 124],
  Chat: [130, 131, 132, 133, 134],
  System: [140, 141, 142, 143, 144],
  RuleSet: [150, 151, 152, 153, 154],
  Assessment: [190, 191, 192],
  Audit: [200],
  AssessmentTemplate: [210, 211, 212],
  LabelTagging: [220, 221, 222, 223],
  PricingConfig: [230, 231, 232, 233, 234],
  ApiCallCost: [240],
  ServiceProvider: [250, 251, 252, 253, 254],
  ServiceModel: [260, 261, 262, 263, 264],
  DashboardPreference: [270],
  FeatureAiConfiguration: [280, 281, 282, 283, 284],
} as const;

/**
 * Convert entitlement IDs from login response to entitlement names
 */
export const convertEntitlementIdsToNames = (ids: number[]): string[] => {
  return ids.map((id) => ENTITLEMENT_ID_TO_NAME[id]).filter(Boolean);
};

/**
 * Convert entitlement names to IDs
 */
export const convertEntitlementNamesToIds = (names: string[]): number[] => {
  return names.map((name) => ENTITLEMENT_NAME_TO_ID[name]).filter(Boolean);
};

// Type definitions
export type EntitlementValue = keyof typeof ENTITLEMENTS;
export type EntitlementName =
  (typeof ENTITLEMENT_ID_TO_NAME)[keyof typeof ENTITLEMENT_ID_TO_NAME];
export type EntityName = keyof typeof ENTITLEMENT_ENTITIES;

export default ENTITLEMENTS;
